import React, { useState, useContext, useEffect } from "react";
import { signOut } from "firebase/auth";
import { auth, db } from "../../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import Context from "../../Context";
import { useTheme } from "../../Context/ThemeContext";
import Link from "../Link";
import { apiFetch } from "../../utils/api";
import { 
  Receipt, 
  FileText, 
  Lock, 
  X,
  LogOut,
  AlertTriangle,
  Shield,
  Zap,
  KeyRound,
  ChevronLeft,
  Home as HomeIcon,
  Target,
  Bell,
  Sun,
  Moon
} from "lucide-react";

// Import page components
import { Home, Profile, Transactions, Bills, Budget, Reports } from "../FinancialPages";

import styles from "./Dashboard.module.scss";

type PageType = "home" | "transactions" | "bills" | "budget" | "reports" | "profile";

// Page subtitles
const pageSubtitles: Record<PageType, string> = {
  home: "Your financial overview and net worth",
  transactions: "View all your transactions, income, and spending",
  bills: "Monitor and manage your recurring bills",
  budget: "Track your spending and manage budget limits",
  reports: "Notification settings and spending reports",
  profile: "Manage your account settings and preferences"
};

const Dashboard: React.FC = () => {
  const { linkSuccess, linkToken, backend, dispatch } = useContext(Context);
  const { toggleTheme, isDark } = useTheme();
  const [currentPage, setCurrentPage] = useState<PageType>("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isBankConnected, setIsBankConnected] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true); // Start as true - checking connection
  const [showConnectPrompt, setShowConnectPrompt] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");

  // Restore backend connection by sending token from Firestore
  const restoreBackendConnection = async (accessToken: string, itemId: string) => {
    try {
      console.log("🔄 Restoring backend connection...");
      const response = await apiFetch("/api/connection/restore", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken, itemId })
      });
      
      if (!response.ok) {
        console.error("Failed to restore backend connection");
        return false;
      }
      
      const data = await response.json();
      console.log("✅ Backend connection restored");
      return data.success;
    } catch (error) {
      console.error("Error restoring backend connection:", error);
      return false;
    }
  };

  // Verify backend has the access token
  const verifyBackendConnection = async (): Promise<boolean> => {
    try {
      const response = await apiFetch("/api/connection/status");
      if (!response.ok) return false;
      const data = await response.json();
      return data.connected;
    } catch (error) {
      console.error('Failed to verify backend connection:', error);
      return false;
    }
  };

  // Function to reload bank connection status from Firestore
  const reloadBankConnection = async () => {
    if (auth.currentUser) {
      console.log("🔄 Dashboard: Reloading bank connection...");
      
      // First check if we have token in Firestore
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : null;
      
      if (userData?.plaidAccessToken) {
        // Verify backend still has the token
        const backendConnected = await verifyBackendConnection();
        if (!backendConnected) {
          console.log("⚠️ Backend lost connection, restoring...");
          // Try to restore backend connection
          const restored = await restoreBackendConnection(
            userData.plaidAccessToken, 
            userData.plaidItemId
          );
          
          if (restored) {
            console.log("✅ Backend connection restored successfully");
            setIsBankConnected(true);
            dispatch({
              type: "SET_STATE",
              state: {
                linkSuccess: true,
                accessToken: userData.plaidAccessToken,
                itemId: userData.plaidItemId,
              },
            });
            return;
          } else {
            console.error("❌ Failed to restore backend connection");
            setIsBankConnected(false);
            dispatch({
              type: "SET_STATE",
              state: {
                linkSuccess: false,
                accessToken: null,
                itemId: null,
              },
            });
            return;
          }
        }
      }
      
      // Poll Firestore for the access token (in case it's not saved yet)
      const maxAttempts = 15; // Try for up to 15 seconds
      let attempts = 0;
      let tokenFound = false;
      
      const pollForToken = async (): Promise<boolean> => {
        while (attempts < maxAttempts && !tokenFound) {
          attempts++;
          
          try {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second between attempts
            
            const userDoc = await getDoc(doc(db, "users", auth.currentUser!.uid));
            
            if (userDoc.exists() && userDoc.data()?.plaidAccessToken) {
              tokenFound = true;
              const userData = userDoc.data();
              
              console.log(`✅ Dashboard: Bank connection detected after ${attempts} attempts`);
              setIsBankConnected(true);
              dispatch({
                type: "SET_STATE",
                state: {
                  linkSuccess: true,
                  accessToken: userData.plaidAccessToken,
                  itemId: userData.plaidItemId,
                },
              });
              
              return true;
            } else {
              console.log(`⏳ Attempt ${attempts}/${maxAttempts} - Token not found yet...`);
            }
          } catch (error) {
            console.error("Error polling for bank connection:", error);
          }
        }
        
        if (!tokenFound) {
          console.warn("⚠️ Dashboard: Max polling attempts reached, token not found");
          setIsBankConnected(false);
          dispatch({
            type: "SET_STATE",
            state: {
              linkSuccess: false,
              accessToken: null,
              itemId: null,
            },
          });
        }
        
        return tokenFound;
      };
      
      await pollForToken();
    }
  };

  // Check if user has connected their bank account
  useEffect(() => {
    const checkBankConnection = async () => {
      if (auth.currentUser) {
        const email = auth.currentUser.email || "";
        setUserEmail(email);
        
        console.log("🔍 Checking bank connection for user:", auth.currentUser.uid);
        
        try {
          // Check Firestore for access token and user data
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          
          console.log("📄 User document exists:", userDoc.exists());
          
          // Document might not exist for new users
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            console.log("🔑 Has plaidAccessToken:", !!userData?.plaidAccessToken);
            
            // Get actual name from Firestore, fallback to display name or email
            if (userData?.name) {
              setUserName(userData.name);
            } else if (auth.currentUser.displayName) {
              setUserName(auth.currentUser.displayName);
            } else {
              // Fallback to email username
              setUserName(email.split("@")[0]);
            }
            
            if (userData?.plaidAccessToken) {
              console.log("✅ Bank is connected in Firestore!");
              
              // Check if backend has the token, restore if needed
              const backendConnected = await verifyBackendConnection();
              if (!backendConnected) {
                console.log("🔄 Backend doesn't have token, restoring...");
                const restored = await restoreBackendConnection(
                  userData.plaidAccessToken,
                  userData.plaidItemId
                );
                
                if (restored) {
                  console.log("✅ Backend connection restored!");
                } else {
                  console.error("❌ Failed to restore backend connection");
                }
              } else {
                console.log("✅ Backend already has token");
              }
              
              setIsBankConnected(true);
              setIsCheckingConnection(false);
              dispatch({
                type: "SET_STATE",
                state: {
                  linkSuccess: true,
                  accessToken: userData.plaidAccessToken,
                  itemId: userData.plaidItemId,
                },
              });
            } else {
              // User document exists but no Plaid token - ensure not connected
              console.log("❌ No Plaid token found");
              setIsBankConnected(false);
              setIsCheckingConnection(false);
              dispatch({
                type: "SET_STATE",
                state: {
                  linkSuccess: false,
                  accessToken: null,
                  itemId: null,
                },
              });
            }
          } else {
            // No Firestore doc (new user) - ensure not connected
            console.log("📝 New user - no Firestore document yet");
            setUserName(auth.currentUser.displayName || email.split("@")[0]);
            setIsBankConnected(false);
            setIsCheckingConnection(false);
            dispatch({
              type: "SET_STATE",
              state: {
                linkSuccess: false,
                accessToken: null,
                itemId: null,
              },
            });
          }
        } catch (error) {
          console.error("Error checking bank connection:", error);
          // Don't block UI if Firestore fails, but assume not connected
          setUserName(email.split("@")[0]);
          setIsBankConnected(false);
          setIsCheckingConnection(false);
          dispatch({
            type: "SET_STATE",
            state: {
              linkSuccess: false,
              accessToken: null,
              itemId: null,
            },
          });
        }
      }
    };

    checkBankConnection();
  }, [dispatch]);

  // Update bank connection status when link succeeds
  // Only trust linkSuccess if we also verify Firestore has the token
  useEffect(() => {
    const verifyConnection = async () => {
      if (linkSuccess && auth.currentUser) {
        // Show loading state while verifying so the "Connect" prompt doesn't flash
        setIsCheckingConnection(true);
        setShowConnectPrompt(false);

        // Poll Firestore for the token (the write from Link may still be in progress)
        const maxAttempts = 10;
        let verified = false;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 500));
          try {
            const userDoc = await getDoc(doc(db, "users", auth.currentUser!.uid));
            if (userDoc.exists() && userDoc.data()?.plaidAccessToken) {
              setIsBankConnected(true);
              setShowConnectPrompt(false);
              setIsCheckingConnection(false);
              console.log(`✅ Dashboard: Bank connection verified after ${attempt} attempt(s)`);
              verified = true;
              break;
            }
          } catch (error) {
            console.error("Error verifying connection:", error);
          }
        }

        if (!verified) {
          // linkSuccess is true but no token appeared in Firestore after polling
          console.warn("⚠️ linkSuccess is true but no token found in Firestore after polling");
          setIsBankConnected(false);
          setIsCheckingConnection(false);
          dispatch({
            type: "SET_STATE",
            state: {
              linkSuccess: false,
              accessToken: null,
              itemId: null,
            },
          });
        }
      } else if (!linkSuccess) {
        // If linkSuccess becomes false, ensure we're marked as not connected
        setIsBankConnected(false);
      }
    };

    verifyConnection();
  }, [linkSuccess, dispatch]);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setShowLogoutConfirm(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const handleShowConnectPrompt = () => {
    setShowConnectPrompt(true);
    
    // Set up the onExit callback to reload data when Plaid Link closes
    dispatch({
      type: "SET_STATE",
      state: {
        onLinkExit: async () => {
          console.log("🔄 Plaid Link closed from Dashboard");
          setShowConnectPrompt(false);
          await reloadBankConnection();
        }
      }
    });
  };

  const handlePageChange = (page: PageType) => {
    // If trying to access financial pages without bank connection, show prompt
    if (!isBankConnected && page !== "profile") {
      handleShowConnectPrompt();
      return;
    }
    
    setCurrentPage(page);
    setMobileMenuOpen(false);
  };

  const renderPage = () => {
    switch (currentPage) {
      case "home":
        return <Home />;
      case "profile":
        return <Profile />;
      case "transactions":
        return <Transactions />;
      case "bills":
        return <Bills />;
      case "budget":
        return <Budget />;
      case "reports":
        return <Reports />;
      default:
        return <Home />;
    }
  };

  return (
    <div className={styles.dashboard}>
      {/* Mobile sidebar backdrop */}
      <div 
        className={`${styles.sidebarBackdrop} ${mobileMenuOpen ? styles.backdropVisible : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      
      {/* Sidebar Navigation */}
      <aside className={`${styles.sidebar} ${mobileMenuOpen ? styles.sidebarOpen : ""} ${sidebarCollapsed ? styles.sidebarCollapsed : ""}`}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logo}>
            {!sidebarCollapsed && <span className={styles.logoText}>ZEBED</span>}
          </div>
          <button 
            className={styles.closeMobileMenu}
            onClick={() => setMobileMenuOpen(false)}
          >
            <X size={24} />
          </button>
        </div>

        <nav className={styles.nav}>
          <button
            className={`${styles.navItem} ${currentPage === "home" ? styles.active : ""}`}
            onClick={() => handlePageChange("home")}
            title="Home"
          >
            <span className={styles.navIcon}><HomeIcon size={20} /></span>
            {!sidebarCollapsed && <span className={styles.navText}>Home</span>}
          </button>

          <div className={styles.navSection}>
            {!sidebarCollapsed && <p className={styles.navSectionTitle}>Finance</p>}
            
            <button
              className={`${styles.navItem} ${currentPage === "transactions" ? styles.active : ""} ${!isBankConnected ? styles.disabled : ""}`}
              onClick={() => handlePageChange("transactions")}
              title={!isBankConnected ? "Connect your bank to access Transactions" : "Transactions"}
              disabled={!isBankConnected}
              data-tooltip={!isBankConnected ? "🔒 Connect your bank to access Transactions" : ""}
            >
              <span className={styles.navIcon}><Receipt size={20} /></span>
              {!sidebarCollapsed && <span className={styles.navText}>Transactions</span>}
              {!isBankConnected && <span className={styles.lockIcon}><Lock size={14} /></span>}
            </button>

            <button
              className={`${styles.navItem} ${currentPage === "bills" ? styles.active : ""} ${!isBankConnected ? styles.disabled : ""}`}
              onClick={() => handlePageChange("bills")}
              title={!isBankConnected ? "Connect your bank to access Bills" : "Bills"}
              disabled={!isBankConnected}
              data-tooltip={!isBankConnected ? "🔒 Connect your bank to access Bills" : ""}
            >
              <span className={styles.navIcon}><FileText size={20} /></span>
              {!sidebarCollapsed && <span className={styles.navText}>Bills</span>}
              {!isBankConnected && <span className={styles.lockIcon}><Lock size={14} /></span>}
            </button>

            <button
              className={`${styles.navItem} ${currentPage === "budget" ? styles.active : ""} ${!isBankConnected ? styles.disabled : ""}`}
              onClick={() => handlePageChange("budget")}
              title={!isBankConnected ? "Connect your bank to access Budget" : "Budget"}
              disabled={!isBankConnected}
              data-tooltip={!isBankConnected ? "🔒 Connect your bank to access Budget" : ""}
            >
              <span className={styles.navIcon}><Target size={20} /></span>
              {!sidebarCollapsed && <span className={styles.navText}>Budget</span>}
              {!isBankConnected && <span className={styles.lockIcon}><Lock size={14} /></span>}
            </button>

            <button
              className={`${styles.navItem} ${currentPage === "reports" ? styles.active : ""} ${!isBankConnected ? styles.disabled : ""}`}
              onClick={() => handlePageChange("reports")}
              title={!isBankConnected ? "Connect your bank to access Reports" : "Reports & Notifications"}
              disabled={!isBankConnected}
              data-tooltip={!isBankConnected ? "🔒 Connect your bank to access Reports" : ""}
            >
              <span className={styles.navIcon}><Bell size={20} /></span>
              {!sidebarCollapsed && <span className={styles.navText}>Reports</span>}
              {!isBankConnected && <span className={styles.lockIcon}><Lock size={14} /></span>}
            </button>
          </div>

     
        </nav>

        <div className={styles.sidebarFooter}>
          {!sidebarCollapsed && (
            <>
              {/* Theme Toggle */}
              <button
                className={styles.themeToggle}
                onClick={toggleTheme}
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                <span className={styles.themeToggleIcon}>
                  {isDark ? <Sun size={18} /> : <Moon size={18} />}
                </span>
                <span className={styles.themeToggleText}>
                  {isDark ? "Light Mode" : "Dark Mode"}
                </span>
              </button>

              <button
                className={`${styles.navItem} ${styles.profileNavItem} ${currentPage === "profile" ? styles.active : ""}`}
                onClick={() => handlePageChange("profile")}
                title="Profile Settings"
              >
                <div className={styles.userInfo}>
                  <div className={styles.userAvatar}>
                    {userName.charAt(0).toUpperCase()}
                  </div>
                  <div className={styles.userDetails}>
                    <p className={styles.userName}>{userName}</p>
                    <p className={styles.userEmail}>{userEmail}</p>
                  </div>
                </div>
              </button>
              <button className={styles.logoutBtn} onClick={handleLogoutClick}>
                <span className={styles.logoutIcon}><LogOut size={18} /></span>
                Sign Out
              </button>
            </>
          )}

          {/* Collapsed state theme toggle */}
          {sidebarCollapsed && (
            <button
              className={styles.themeToggleCollapsed}
              onClick={toggleTheme}
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          )}

          {/* Collapse Toggle Button - Inside Footer */}
          <button 
            className={styles.collapseToggle}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? "Z" : <ChevronLeft size={20} />}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={styles.mainContent}>
        {/* Top Bar */}
        <div className={styles.topBar}>
          <button 
            className={styles.hamburger}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className={styles.pageTitleContainer}>
            <h1 className={styles.pageTitle}>
              {currentPage.charAt(0).toUpperCase() + currentPage.slice(1)}
            </h1>
            <p className={styles.pageSubtitle}>
              {pageSubtitles[currentPage]}
            </p>
          </div>

          {!isBankConnected && (
            <div className={styles.connectBanner}>
              <AlertTriangle size={16} className={styles.warningIcon} />
              <span>Bank not connected</span>
            </div>
          )}
        </div>

        {/* Page Content */}
        <div className={styles.pageContent}>
          {renderPage()}
        </div>
      </main>

      {/* Connect Bank Prompt Modal */}
      {showConnectPrompt && (
        <div className={styles.modalOverlay} onClick={() => setShowConnectPrompt(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button 
              className={styles.modalClose}
              onClick={() => setShowConnectPrompt(false)}
            >
              <X size={24} />
            </button>

            <div className={styles.promptHeader}>
              <span className={styles.promptIcon}>
                <Lock size={64} />
              </span>
              <h2>Connect Your Bank Account</h2>
            </div>

            <p className={styles.promptText}>
              To access your financial data and insights, you need to connect your bank account first.
              This is a secure, one-time setup process powered by Plaid.
            </p>

            <div className={styles.promptFeatures}>
              <div className={styles.promptFeature}>
                <Shield size={20} />
                <span>Bank-level 256-bit encryption</span>
              </div>
              <div className={styles.promptFeature}>
                <Zap size={20} />
                <span>Real-time transaction syncing</span>
              </div>
              <div className={styles.promptFeature}>
                <KeyRound size={20} />
                <span>Your credentials never stored</span>
              </div>
            </div>

            <div className={styles.promptActions}>
              {!backend ? (
                <div className={styles.errorMessage}>
                  Backend server is not running. Please start your server first.
                </div>
              ) : linkToken == null && backend ? (
                <div className={styles.errorMessage}>
                  Unable to generate connection token. Please check your configuration.
                </div>
              ) : linkToken === "" ? (
                <button className={styles.connectBtn} disabled>
                  Loading...
                </button>
              ) : (
                <Link />
              )}
              
              <button 
                className={styles.cancelBtn}
                onClick={() => setShowConnectPrompt(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className={styles.modalOverlay} onClick={handleLogoutCancel}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button 
              className={styles.modalClose}
              onClick={handleLogoutCancel}
            >
              <X size={24} />
            </button>

            <div className={styles.logoutModalHeader}>
              <span className={styles.logoutModalIcon}>
                <LogOut size={48} />
              </span>
              <h2>Sign Out?</h2>
            </div>

            <p className={styles.logoutModalText}>
              Are you sure you want to sign out? You'll need to log back in to access your financial dashboard.
            </p>

            <div className={styles.logoutModalActions}>
              <button 
                className={styles.logoutConfirmBtn}
                onClick={handleLogoutConfirm}
              >
                <LogOut size={18} />
                Yes, Sign Out
              </button>
              <button 
                className={styles.logoutCancelBtn}
                onClick={handleLogoutCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay while checking bank connection */}
      {isCheckingConnection && currentPage !== "profile" && (
        <div className={styles.blurOverlay}>
          <div className={styles.blurContent}>
            <div className={styles.loadingSpinner}></div>
            <h3>Loading your financial data...</h3>
            <p>Please wait while we connect to your accounts.</p>
          </div>
        </div>
      )}

      {/* Blur Overlay for Unconnected Pages */}
      {!isCheckingConnection && !isBankConnected && currentPage !== "profile" && (
        <div className={styles.blurOverlay}>
          <div className={styles.blurContent}>
            <span className={styles.blurIcon}>
              <Lock size={72} />
            </span>
            <h3>Connect Your Bank to Continue</h3>
            <p>This section requires a connected bank account to display your financial data.</p>
            <button 
              className={styles.blurConnectBtn}
              onClick={handleShowConnectPrompt}
            >
              Connect Bank Account
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
